// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file SelectableCard.tsx
 * @input Uses Card, useClickableContainer, StyleX
 * @output Exports SelectableCard component and SelectableCardProps
 * @position Interactive card for toggle selection
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/SelectableCard/SelectableCard.doc.mjs (props table, features)
 * - /packages/core/src/SelectableCard/index.ts (exports if types change)
 * - /apps/storybook/stories/SelectableCard.stories.tsx (storybook stories)
 * - /packages/cli/assets/templates/blocks/components/Card/SelectableCardShowcase.tsx (showcase block)
 * - /packages/cli/assets/templates/blocks/components/Card/SelectableCardMulti.tsx (block)
 * - /packages/cli/assets/templates/blocks/components/Card/SelectableCardElevated.tsx (block)
 *
 * Composes Card for all visual styling. Adds selection state with
 * an inset box-shadow (zero layout jitter) and useClickableContainer
 * for safe nested interactive elements.
 *
 * A hidden <input type="checkbox"> inside the card provides the accessible
 * role, label, and checked state — the card surface itself has no role/tabIndex.
 * Space toggles it natively; Enter is wired up as an additional toggle key.
 *
 * For static display, use Card.
 * For navigation or action cards, use ClickableCard.
 */
import { useRef, useCallback } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { colorVars, durationVars, easeVars } from "../theme/tokens.stylex.js";
import { mergeProps, mergeRefs } from "../utils/index.js";
import { Card } from "../Card/Card.js";
import { useClickableContainer } from "../hooks/useClickableContainer.js";
import { themeProps } from "../utils/themeProps.js";

// =============================================================================
// Styles — selection + interaction; Card handles the rest
// =============================================================================
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const styles = {
  interactive: {
    kVAEAm: "x1n2onr6",
    kkrTdU: "x1ypdohk",
    k1ekBW: "x16mfhj0",
    kIyJzY: "xuedmi6",
    kAMwcw: "xlr8y92",
    kInvED: "x1hl8ikr",
    $$css: true
  },
  focusWithin: {
    kRYL1X: "x1irc7jg",
    koJ47v: "xdjuwb3",
    $$css: true
  },
  overlay: {
    k5JduY: "x1s928wv",
    kwXMNM: "x1j6awrg",
    kv0HGH: "xarstr8",
    kUfP38: "x1k48kgn",
    kloYau: "x2q1x1w",
    kRicXK: "x1ywzrc5",
    kPNhGg: "x97pup0",
    kA8PQs: "x1dlmc9c",
    ks3ayO: "xyhc2n1",
    kAcZsS: "x1k7wiig",
    $$css: true
  },
  hoverOnPointer: {
    kJs8I2: "xi14tyy",
    $$css: true
  },
  disabled: {
    kkrTdU: "x1h6gzvc",
    kSiTet: "xbyyjgo",
    $$css: true
  },
  selected: {
    kVAM5u: "xad5do",
    "--_card-ring": "xr9ms2c",
    $$css: true
  },
  selectedBlue: {
    kVAM5u: "xlee4gx",
    "--_card-ring": "xgwwt2v",
    $$css: true
  },
  selectedCyan: {
    kVAM5u: "xb1xfl",
    "--_card-ring": "xcdi0x3",
    $$css: true
  },
  selectedGray: {
    kVAM5u: "x1w9ec3u",
    "--_card-ring": "x1m1zkwp",
    $$css: true
  },
  selectedGreen: {
    kVAM5u: "xy90a9n",
    "--_card-ring": "x1485227",
    $$css: true
  },
  selectedOrange: {
    kVAM5u: "xloqurj",
    "--_card-ring": "x1cclxpp",
    $$css: true
  },
  selectedPink: {
    kVAM5u: "x1czkbn8",
    "--_card-ring": "xzs6dn4",
    $$css: true
  },
  selectedPurple: {
    kVAM5u: "xq98wne",
    "--_card-ring": "x9zgi83",
    $$css: true
  },
  selectedRed: {
    kVAM5u: "xjqgoec",
    "--_card-ring": "xo8718j",
    $$css: true
  },
  selectedTeal: {
    kVAM5u: "x1r9gxr1",
    "--_card-ring": "xp4e5fy",
    $$css: true
  },
  selectedYellow: {
    kVAM5u: "x1kpnwn1",
    "--_card-ring": "x1glw7ee",
    $$css: true
  }
};
const selectedStyleForVariant = variant => {
  switch (variant) {
    case 'default':
    case 'transparent':
    case 'muted':
      return styles.selected;
    case 'blue':
      return styles.selectedBlue;
    case 'cyan':
      return styles.selectedCyan;
    case 'gray':
      return styles.selectedGray;
    case 'green':
      return styles.selectedGreen;
    case 'orange':
      return styles.selectedOrange;
    case 'pink':
      return styles.selectedPink;
    case 'purple':
      return styles.selectedPurple;
    case 'red':
      return styles.selectedRed;
    case 'teal':
      return styles.selectedTeal;
    case 'yellow':
      return styles.selectedYellow;
  }
};

// =============================================================================
// Props
// =============================================================================

// =============================================================================
// Component
// =============================================================================

/**
 * A card that toggles between selected and unselected states.
 *
 * Composes Card for visual styling and adds selection state with
 * an inset box-shadow (zero layout jitter vs plain Card). Supports
 * hover, pressed, focus, and disabled states.
 *
 * A visually-hidden <input type="checkbox"> inside the card provides
 * the accessible role, label, and checked state. The card surface
 * is a plain <div> — no role or tabIndex on the container.
 *
 * @compositionHint Use for multi-select or single-select card groups.
 * Manage selection state externally — use a Set for multi-select
 * or a single value for radio-style selection.
 * For navigation/action cards, use ClickableCard instead.
 *
 * @example
 * ```
 * <SelectableCard
 *   label="Option A"
 *   isSelected={selected === 'a'}
 *   onChange={() => setSelected('a')}>
 *   <Text type="body" weight="bold">Option A</Text>
 * </SelectableCard>
 * ```
 */
export function SelectableCard({
  label,
  isSelected,
  onChange,
  onClick: onClickProp,
  onMouseUp: onMouseUpProp,
  isDisabled = false,
  children,
  padding,
  variant = 'default',
  elevation = 'none',
  width,
  height,
  maxWidth,
  ref,
  xstyle: xstyleProp,
  className: classNameProp,
  style,
  ...props
}) {
  const containerRef = useRef(null);
  const interactiveRef = useRef(null);
  const handleClick = useCallback(_event => {
    if (!isDisabled) {
      onChange(!isSelected);
    }
  }, [isDisabled, isSelected, onChange]);

  // The focusable control is a native checkbox, which toggles on Space but
  // ignores Enter. Add Enter as an extra toggle key; Space keeps its native
  // handling, so we deliberately do not toggle on Space here (would double).
  const handleKeyDown = useCallback(event => {
    if (!isDisabled && event.key === 'Enter') {
      event.preventDefault();
      onChange(!isSelected);
    }
  }, [isDisabled, isSelected, onChange]);
  const {
    onClick,
    onMouseUp
  } = useClickableContainer({
    containerRef,
    interactiveRef,
    onClick: handleClick,
    disabled: isDisabled
  });
  const composedOnClick = onClickProp ? e => {
    onClick(e);
    onClickProp(e);
  } : onClick;
  const composedOnMouseUp = onMouseUpProp ? e => {
    onMouseUp(e);
    onMouseUpProp(e);
  } : onMouseUp;
  return /*#__PURE__*/_jsxs(Card, {
    ref: mergeRefs(ref, containerRef),
    width: width,
    height: height,
    maxWidth: maxWidth,
    padding: padding,
    variant: variant,
    elevation: elevation,
    ...mergeProps(themeProps('selectable-card', {
      variant,
      selected: isSelected ? 'true' : 'false'
    }), {
      className: classNameProp,
      style
    }),
    xstyle: [styles.interactive, styles.focusWithin, isSelected && selectedStyleForVariant(variant), !isDisabled && styles.overlay, !isDisabled && styles.hoverOnPointer, isDisabled && styles.disabled, xstyleProp],
    onClick: !isDisabled ? composedOnClick : undefined,
    onMouseUp: !isDisabled ? composedOnMouseUp : undefined,
    ...props,
    children: [/*#__PURE__*/_jsx("input", {
      ref: interactiveRef,
      type: "checkbox",
      checked: isSelected,
      "aria-label": label,
      disabled: isDisabled,
      onChange: () => onChange(!isSelected),
      onKeyDown: handleKeyDown,
      ...{
        className: "x10l6tqk x1i1rx1s xjm9jq1 x1717udv xkdpibf xb3r6kr xzpqnlu xuxw1ft xc342km"
      }
    }), children]
  });
}
SelectableCard.displayName = 'SelectableCard';